import { ChangeEvent, useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useSelector } from "react-redux";
import {
    Button,
    DropdownProps,
    Icon,
    Input,
    InputOnChangeData,
    Label,
    Modal,
    Select,
    TextArea,
} from "semantic-ui-react";
import { toast } from "../..";
import { agent } from "../../api/agent";
import { TranslateProps } from "../../i18n/TranslateProps";
import {
    AuthorizationType,
    authorizationTypeOptions,
    IAuthorization,
    newAuthorizationInit,
} from "../../models/authorization";
import { boatmasterLicenceOptions, ICertificate } from "../../models/certificate";
import { ICrewMember } from "../../models/crewMember";
import { AppState } from "../../store/configureStore";
import { ucqRiverRangeProps } from "../../models/riverRanges";
import { DateInput } from "../../components/DateInput/DateInput";
import { constructDate, IDate } from "../../models/common";

type Props = {
    open: boolean;
    close: () => void;
    activeAuthorizations: IAuthorization[];
    targetCertificate: ICertificate;
    crewMember: ICrewMember;
    fetchCrewMember: () => Promise<void>;
    isRenew?: boolean;
};

export const AddAuthorizationsForm = ({
    open,
    close,
    activeAuthorizations,
    targetCertificate,
    crewMember,
    fetchCrewMember,
    isRenew,
}: Props) => {
    const user = useSelector((state: AppState) => state.user);
    const intl = useIntl();
    const [newAuth, setNewAuth] = useState(
        isRenew
            ? activeAuthorizations.find(a => a.type === AuthorizationType.B) ?? newAuthorizationInit
            : newAuthorizationInit
    );
    const [loading, setLoading] = useState(false);
    const stretchRegex: RegExp = /^[A-Z][A-Z-]-\d\d\d\d-\d\d\d\d$/;
    const [invalidStretch, setInvalidStretch] = useState(false);
    const [manualStretches, setManualStretches] = useState<string>("");
    const [startDate, setStartDate] = useState<IDate>({
        day: new Date().getDate(),
        month: new Date().getMonth() + 1,
        year: new Date().getFullYear(),
    });

    const availableAuths = authorizationTypeOptions.filter(
        x => !activeAuthorizations.some(a => a.type === x.value)
    );
    const availableRiverKilometers = ucqRiverRangeProps
        .filter(
            r =>
                !targetCertificate.stretchesRiverKilometers.includes(r.value as number) &&
                !activeAuthorizations.flatMap(a => a.ucqRiverKilometers).includes(r.value as number)
        )
        // only RH-0335-0425 and RH-0498-0592 are available
        .filter(r => r.value === 15 || r.value === 12);
    if (
        availableRiverKilometers.length > 0 &&
        !availableAuths.some(a => a.value === AuthorizationType.S)
    )
        availableAuths.push({
            key: AuthorizationType.S,
            text: "sailingStretches",
            value: AuthorizationType.S,
            code: "S",
        });

    const handleAddAuthorization = async () => {
        setLoading(true);
        try {
            newAuth.linkedQualificationID = targetCertificate.qualificationID;
            newAuth.issuingAuthority = targetCertificate.issuingAuthority;
            newAuth.startDate =
                newAuth.type === AuthorizationType.B
                    ? constructDate(startDate)
                    : targetCertificate.startDate;
            newAuth.endDate = targetCertificate.endDate;
            newAuth.status = targetCertificate.status;
            newAuth.crewMemberGuid = crewMember.guid;
            newAuth.ucqManualRiverKilometers = manualStretches;
            isRenew
                ? await agent.Certificates.renewAuth(newAuth)
                : await agent.Certificates.addAuth(newAuth);
            toast(
                intl.formatMessage(
                    isRenew ? { id: "authorizationRenewedMsg" } : { id: "authorizationAddedMsg" }
                ),
                true
            );
            fetchCrewMember();
        } catch {
            toast(
                intl.formatMessage(
                    isRenew ? { id: "authorizationRenewFail" } : { id: "authorizationAddFail" }
                ),
                false
            );
        } finally {
            setLoading(false);
            close();
        }
    };

    useEffect(() => {
        setNewAuth(a => ({ ...a, issuingAuthority: user.authority }));
    }, [user.authority]);

    const validateStretch = (stretch: string) => {
        if (stretchRegex.exec(stretch)) {
            setInvalidStretch(false);
        }
    };

    const handleAddManualRanges = (e: any) => {
        if (e.key === "Enter" || e.charCode === 13) {
            setInvalidStretch(false);
            if (stretchRegex.exec(e.target.value)) {
                setManualStretches(`${manualStretches}${e.target.value};`);
                setNewAuth({
                    ...newAuth,
                    ucqManualRiverKilometers:
                        newAuth.type === AuthorizationType.S
                            ? `${manualStretches}${e.target.value};`
                            : "",
                });
            } else {
                setInvalidStretch(true);
            }
        }
    };

    const handleRemoveStretch = (s: string) => {
        setManualStretches(manualStretches.replace(`${s};`, ""));
        setNewAuth({
            ...newAuth,
            ucqManualRiverKilometers:
                newAuth.type === AuthorizationType.S ? manualStretches.replace(`${s};`, "") : "",
        });
    };

    return (
        <Modal
            open={open}
            onClose={() => {
                close();
                setNewAuth(newAuthorizationInit);
            }}
            dimmer="blurring"
            size="small"
            closeOnDimmerClick={!loading}
        >
            <Modal.Header>{intl.formatMessage({ id: "addAuthorization" })}</Modal.Header>
            <Modal.Content>
                <form className="add-authorization-form">
                    <section className="input-container">
                        <label>{intl.formatMessage({ id: "authorizationType" })}</label>
                        <Select
                            search
                            options={
                                isRenew
                                    ? TranslateProps(intl, authorizationTypeOptions)
                                    : TranslateProps(intl, availableAuths)
                            }
                            // only Basel city stretch can be renewed
                            value={isRenew ? AuthorizationType.B : newAuth.type}
                            onChange={(_e, data: any) =>
                                setNewAuth(a => ({ ...a, type: +data.value }))
                            }
                            disabled={isRenew}
                        />
                    </section>
                    {newAuth.type === AuthorizationType.S && (
                        <>
                            <section className="input-container">
                                <label>{intl.formatMessage({ id: "range" })}</label>
                                <Select
                                    search
                                    multiple
                                    options={availableRiverKilometers}
                                    value={newAuth.ucqRiverKilometers}
                                    onChange={(_e, data: any) =>
                                        setNewAuth({
                                            ...newAuth,
                                            ucqRiverKilometers: data.value,
                                        })
                                    }
                                />
                            </section>

                            <section className="input-container">
                                <label>{intl.formatMessage({ id: "manualAddSections" })}</label>
                                <Input
                                    className={invalidStretch ? "invalidStretch" : ""}
                                    onChange={(
                                        _e: ChangeEvent<HTMLInputElement>,
                                        data: InputOnChangeData
                                    ) => {
                                        validateStretch(data.value);
                                    }}
                                    onKeyPress={(e: any) => {
                                        handleAddManualRanges(e);
                                    }}
                                />
                                {manualStretches
                                    .split(";")
                                    // remove last element (empty)
                                    .slice(0, -1)
                                    .map((s, i) => (
                                        <Label as="a" key={i}>
                                            {s}
                                            <Icon
                                                name="delete"
                                                onClick={() => handleRemoveStretch(s)}
                                            />
                                        </Label>
                                    ))}
                                {invalidStretch && (
                                    <label className="invalidLabel">
                                        {intl.formatMessage({ id: "wrongStretchCodeFormat" })}
                                    </label>
                                )}
                            </section>
                        </>
                    )}
                    {newAuth.type === AuthorizationType.B && (
                        <>
                            <div className="form-row two">
                                <section className="input-container">
                                    <label>
                                        {intl.formatMessage({ id: "type" })}
                                        <span className="input-required">*</span>
                                    </label>
                                    <Select
                                        options={TranslateProps(intl, boatmasterLicenceOptions)}
                                        value={newAuth.boatmasterLicenceType}
                                        onChange={(_e, data: DropdownProps) =>
                                            setNewAuth({
                                                ...newAuth,
                                                boatmasterLicenceType: data.value as number,
                                            })
                                        }
                                    />
                                </section>
                                <section className="input-container">
                                    <label>
                                        {intl.formatMessage({ id: "startDate" })}
                                        <span className="input-required">*</span>
                                    </label>
                                    <DateInput
                                        date={startDate}
                                        setDate={setStartDate}
                                        extendedYears
                                    />
                                </section>
                            </div>
                            <section className="input-container">
                                <label>{intl.formatMessage({ id: "comments" })}</label>
                                <TextArea
                                    value={newAuth.comments ?? ""}
                                    onChange={e =>
                                        setNewAuth({ ...newAuth, comments: e.target.value })
                                    }
                                />
                            </section>
                            <section className="input-container">
                                <label>
                                    {intl.formatMessage({ id: "sectionsOfTheRhine" })}
                                    <span className="input-required">*</span>
                                </label>
                                <Select
                                    options={[{ key: 1, text: "RH-0163.60-0166.53", value: 1 }]}
                                    value={1}
                                    closeOnChange
                                    disabled
                                />
                            </section>
                        </>
                    )}
                </form>
            </Modal.Content>
            <Modal.Actions>
                <Button
                    primary
                    onClick={() => handleAddAuthorization()}
                    content={
                        isRenew
                            ? intl.formatMessage({ id: "renew" })
                            : intl.formatMessage({ id: "add" })
                    }
                    fluid
                    loading={loading}
                    disabled={
                        loading ||
                        !targetCertificate ||
                        newAuth.type < 0 ||
                        (newAuth.type === AuthorizationType.S &&
                            manualStretches.length === 0 &&
                            newAuth.ucqRiverKilometers.length === 0) ||
                        (newAuth.type === AuthorizationType.B &&
                            (!startDate || !newAuth.boatmasterLicenceType))
                    }
                />
            </Modal.Actions>
        </Modal>
    );
};
